import { useCallback, useEffect, useRef, useState } from 'react';
import { orderBroadcast } from '../services/orderBroadcastService';
import dayjs from 'dayjs';

export const EMPTY_ORDER_DATA = {
    orderItems: [],
    totals: {
        subtotal: 0,
        vat: 0,
        grandTotal: 0,
        cash: 0,
        card: 0,
        remaining_amount: 0,
    },
    customerName: '',
    customer: null,
    orderDate: '',
};

export const formatOrderBroadcastData = (data = {}) => {
    const rawItems = data.orderItems || data.items || data.cartItems || [];

    const formattedItems = rawItems.map((item) => {
        const qty = item.quantity === '' ? 1 : Number(item.quantity ?? item.order_item_quantity ?? 1);
        const unitPrice = parseFloat(
            item.unit_price ?? item.order_item_unit_price ?? 0
        );
        const isTaxable = item.taxable === 'Taxable' || item.vat || item.vat_percentage > 0;
        const vatPercent = typeof item.vat === 'number' ? item.vat : (item.vat_percentage ?? (isTaxable ? 20 : 0));
        const vatAmount = item.vat_amount ?? item.order_item_vat ?? (isTaxable ? unitPrice * qty * 0.20 : 0);
        const total = item.order_item_total ?? item.totalWithVAT ?? (unitPrice * qty + (isTaxable ? vatAmount : 0));

        return {
            ...item,
            item_name: item.item_name || item.product_name || item.name || '',
            product_name: item.product_name || item.item_name || item.name || '',
            quantity: qty,
            order_item_quantity: qty,
            unit_price: unitPrice,
            order_item_unit_price: unitPrice,
            vat: vatPercent,
            vat_percentage: vatPercent,
            vat_amount: vatAmount,
            order_item_vat: vatAmount,
            order_item_total: total,
            totalWithVAT: total,
        };
    });

    const subtotal = data.totals?.subtotal ?? formattedItems.reduce((acc, it) => acc + it.unit_price * it.quantity, 0);
    const vat = data.totals?.vat ?? formattedItems.reduce((acc, it) => acc + (it.order_item_vat || 0), 0);
    const grandTotal = data.totals?.grandTotal ?? subtotal + vat;

    const customerName =
        data.customerName ||
        data.customer?.name ||
        data.customer?.title ||
        '';

    const orderDate = data.orderDate || dayjs().format('DD-MM-YYYY');

    return {
        orderItems: formattedItems,
        totals: {
            subtotal: Number(subtotal) || 0,
            vat: Number(vat) || 0,
            grandTotal: Number(grandTotal) || 0,
            cash: Number(data.totals?.cash) || 0,
            card: Number(data.totals?.card) || 0,
            remaining_amount: Number(data.totals?.remaining_amount) || 0,
            discount: Number(data.totals?.discount) || 0,
        },
        customerName,
        customer: data.customer || (customerName ? { title: customerName } : null),
        orderDate,
    };
};

export const broadcastOrderData = (data) => {
    const payload = formatOrderBroadcastData(data);
    orderBroadcast.broadcast(payload);
    return payload;
};


export const clearOrderBroadcast = () => {
    orderBroadcast.clear();
};

export const useOrderBroadcast = (broadcastOptions) => {
    const [orderData, setOrderData] = useState(() => {
        return orderBroadcast.getStoredData?.() || EMPTY_ORDER_DATA;
    });

    const prevPayloadRef = useRef(null);

    useEffect(() => {
        if (!broadcastOptions) return;
        if (broadcastOptions.enabled === false) return;

        const payload = formatOrderBroadcastData(broadcastOptions);
        const serialized = JSON.stringify(payload);

        if (prevPayloadRef.current !== serialized) {
            prevPayloadRef.current = serialized;
            orderBroadcast.broadcast(payload);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        broadcastOptions?.orderItems,
        broadcastOptions?.totals?.subtotal,
        broadcastOptions?.totals?.vat,
        broadcastOptions?.totals?.grandTotal,
        broadcastOptions?.totals?.cash,
        broadcastOptions?.totals?.card,
        broadcastOptions?.totals?.remaining_amount,
        broadcastOptions?.customerName,
        broadcastOptions?.customer,
        broadcastOptions?.orderDate,
        broadcastOptions?.enabled,
    ]);

    useEffect(() => {
        const unsubscribe = orderBroadcast.subscribe((data) => {
            if (data) {
                setOrderData(data);
            }
        });

        return unsubscribe;
    }, []);

    const broadcast = useCallback((data) => {
        return broadcastOrderData(data);
    }, []);

    const clear = useCallback(() => {
        clearOrderBroadcast();
    }, []);

    return {
        ...orderData,
        orderItems: orderData?.orderItems || [],
        totals: orderData?.totals || EMPTY_ORDER_DATA.totals,
        orderDate: orderData?.orderDate || '',
        customer: orderData?.customer || null,
        broadcastOrderData: broadcast,
        clearOrderBroadcast: clear,
    };
};